import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Center,
  Heading,
  HStack,
  Pressable,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import DatePicker from "react-datepicker";
import moment from "moment";
import qs from "qs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RiFolderWarningLine } from "react-icons/ri";
import colors from "../../../constants/colors";
import DatePickerCustomInput from "../../../components/datePickerCustomInput";
import { StateContext, StatusContext } from "../../../contexts";
import API from "../../../api";
import Tour from "../../../components/tour";

function AgencyPaymentManagementPage() {
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialDate, setInitialDate] = useState(
    searchParams.get("initialDate")
      ? moment(searchParams.get("initialDate")).toDate()
      : moment().startOf("day").toDate(),
  );
  const [finalDate, setFinalDate] = useState(
    searchParams.get("finalDate")
      ? moment(searchParams.get("finalDate")).toDate()
      : moment().add(1, "week").endOf("day").milliseconds(0).toDate(),
  );
  const [tours, setTours] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get("tour")) {
      window.alert(
        `Tu viaje ha sido creado con el folio #${searchParams.get("tour")}.`,
      );
    }
  }, [searchParams]);

  const getTours = useCallback(
    async (signal) => {
      try {
        setIsLoading(true);
        const query = qs.stringify({
          itinerary: {
            type: "origin",
            isReturn: false,
            departureDate: {
              $gte: initialDate.toISOString(),
              $lte: finalDate.toISOString(),
            },
          },
        });
        const {
          data: {
            data: _tours,
            meta: { total: _total },
          },
        } = await API(state.jwt).get(
          `/tours/agency?${query}`,
          signal ? { signal } : {},
        );
        setTotal(_total);
        setTours(_tours);
      } catch (error) {
        status.updateErrorMessage(error);
      }
      setIsLoading(false);
    },
    [initialDate, finalDate, state.jwt, status],
  );

  const updateSearchParams = useCallback(() => {
    navigate(
      {
        search: qs.stringify({
          initialDate: initialDate.toISOString(),
          finalDate: finalDate.toISOString(),
        }),
      },
      { replace: true },
    );
  }, [navigate, initialDate, finalDate]);

  useEffect(() => {
    updateSearchParams();
    const controller = new AbortController();
    getTours(controller.signal);
    return () => {
      controller.abort();
    };
  }, [getTours, updateSearchParams]);

  const onApplyFilters = () => {
    updateSearchParams();
    getTours();
  };

  const isFilterApplied = useMemo(
    () =>
      searchParams.get("initialDate") === initialDate.toISOString() &&
      searchParams.get("finalDate") === finalDate.toISOString(),
    [searchParams, initialDate, finalDate],
  );

  return (
    <VStack>
      <Helmet>
        <title>Gestión de cobros</title>
      </Helmet>
      <HStack paddingY={8}>
        <Heading color={colors.secondary} fontSize="2xl">
          Gestión de cobros
        </Heading>
      </HStack>
      <Text>Fecha de inicio de viaje</Text>
      <Spacer height={2} />
      <HStack space={5} alignItems="flex-end" zIndex={1}>
        <DatePicker
          selected={initialDate}
          onChange={setInitialDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          maxDate={finalDate}
          dateFormat="yyyy-MM-dd"
        />
        <DatePicker
          selected={finalDate}
          onChange={setFinalDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          minDate={initialDate}
          dateFormat="yyyy-MM-dd"
        />
        <Pressable
          backgroundColor={colors.primary}
          paddingX={4}
          paddingY={1}
          borderRadius={5}
          onPress={onApplyFilters}
          disabled={isFilterApplied}>
          <Text color={colors.white}>Aplicar</Text>
        </Pressable>
      </HStack>
      <Spacer height={2} />
      <Text fontSize="sm" textAlign="right">
        Se han encontrado {total} resultado{total !== 1 && "s"}
      </Text>
      <Spacer height={5} />
      {isLoading ? (
        <Center height={300}>
          <Spinner size="lg" color={colors.primary} />
        </Center>
      ) : tours.length ? (
        <HStack flexWrap="wrap">
          {tours.map((tour, index) => (
            <Tour key={String(tour.id)} tour={tour} index={index} />
          ))}
        </HStack>
      ) : (
        <Center height={300}>
          <RiFolderWarningLine color={colors.secondary} fontSize={100} />
          <Text color={colors.secondary}>No hay información disponible</Text>
        </Center>
      )}
    </VStack>
  );
}

export default AgencyPaymentManagementPage;
